/**
 * historyAtoms - Action history and undo/redo state for the graph explorer.
 *
 * Records user actions (node double-click, select/remove relationship; node/edge/pane click excluded)
 * with a snapshot of state before each action, so we can undo (restore state)
 * and redo (re-apply the action).
 */
import { atom, type Atom, type PrimitiveAtom, type WritableAtom } from 'jotai';

import { ExecuteQueryAtom } from '../../../../../state/ExecuteActionAtom';
import {
	CypherQueryAtom,
	ObjectsQueryResultAtom,
	ObjectsQueryRunningAtom,
	QueryAbortControllerAtom,
} from '../../../../../state/LabsAtoms';
import {
	DiscoverForNodeAtom,
	ToggleRelationshipAtom,
	RemoveRelationshipAtom,
} from '../actions/explorationActions';
import { EnterFocusModeAtom } from '../actions/focusActions';
import { DrillIntoChildAtom } from '../actions/navigationActions';
import type { ObjectNodeData } from '../nodes/ObjectNode';
import type { ObjectSelection } from '../panels/ObjectMetadataPanel';
import type { ExplorationDirection } from '../services/cypherQueryService';
import type { AGGResponse } from '../useObjectsGraph';
import { BaseNodeIdsAtom } from './baseGraphAtoms';
import {
	DiscoveredOutgoingAtom,
	DiscoveredIncomingAtom,
	DiscoveryStatusAtom,
	ExplorationErrorAtom,
	LoadingRelKeysAtom,
} from './explorationAtoms';
import type { SelectedRelationship } from './focusAtoms';
import {
	FocusedNodeAtom,
	ActiveNodeAtom,
	NavigationPathAtom,
	SelectedRelationshipsAtom,
	FocusDirectionAtom,
} from './focusAtoms';
import { ObjectSelectionAtom } from './selectionAtoms';

// ============================================================================
// Action types (discriminated union)
// ============================================================================

export type GraphExplorerAction =
	| { type: 'nodeDoubleClick'; nodeData: ObjectNodeData }
	| {
			type: 'selectRelationship';
			sourceNode: ObjectNodeData;
			relType: string;
			direction: ExplorationDirection;
	  }
	| { type: 'removeRelationship'; key: string }
	| { type: 'executeQuery'; cypherQuery: string };

// ============================================================================
// State snapshot (for undo)
// ============================================================================

export interface HistoryStateSnapshot {
	selection: ObjectSelection;
	focusedNode: ObjectNodeData | null;
	selectedRelationships: SelectedRelationship[];
	navigationPath: ObjectNodeData[];
	activeNode: ObjectNodeData | null;
	/** Cypher query text before the action (only captured for executeQuery actions). */
	cypherQuery?: string;
	/** Query result before the action (only captured for executeQuery actions). */
	queryResult?: AGGResponse | null;
}

export interface HistoryEntry {
	action: GraphExplorerAction;
	stateBefore: HistoryStateSnapshot;
}

// ============================================================================
// Source atoms
// ============================================================================

/** Action history: entries in order. Each entry has the action and state before it. */
export const ActionHistoryAtom: PrimitiveAtom<HistoryEntry[]> & {
	init: HistoryEntry[];
} = atom<HistoryEntry[]>([]);

/** Redo stack: actions that were undone. Cleared when a new action is recorded. */
export const RedoStackAtom: PrimitiveAtom<GraphExplorerAction[]> & {
	init: GraphExplorerAction[];
} = atom<GraphExplorerAction[]>([]);

// ============================================================================
// Write atom: record an action (clears redo stack)
// ============================================================================

/**
 * Write-only atom that captures the current graph + query state and pushes an
 * `executeQuery` history entry. Call this before executing any cypher query so
 * that the user can undo back to the previous state (including the explore node).
 */
export const PushQueryHistoryAtom: WritableAtom<null, [cypherQuery: string], void> & {
	init: null;
} = atom(null, (get, set, cypherQuery: string) => {
	const stateBefore: HistoryStateSnapshot = {
		selection: get(ObjectSelectionAtom),
		focusedNode: get(FocusedNodeAtom),
		selectedRelationships: get(SelectedRelationshipsAtom),
		navigationPath: get(NavigationPathAtom),
		activeNode: get(ActiveNodeAtom),
		cypherQuery: get(CypherQueryAtom),
		queryResult: get(ObjectsQueryResultAtom),
	};

	set(PushActionHistoryAtom, {
		action: { type: 'executeQuery', cypherQuery },
		stateBefore,
	});
});

export const PushActionHistoryAtom: WritableAtom<null, [entry: HistoryEntry], void> & {
	init: null;
} = atom(null, (get, set, entry: HistoryEntry) => {
	set(RedoStackAtom, []);
	set(ActionHistoryAtom, (prev) => {
		const next = [...prev, entry];
		return next;
	});
});

// ============================================================================
// Derived: can undo / can redo
// ============================================================================

export const CanUndoAtom: Atom<boolean> = atom((get) => get(ActionHistoryAtom).length > 0);
export const CanRedoAtom: Atom<boolean> = atom((get) => get(RedoStackAtom).length > 0);

// ============================================================================
// Undo: restore state before last action, push action to redo stack
// ============================================================================

export const UndoAtom: WritableAtom<null, [], Promise<void>> & {
	init: null;
} = atom(null, async (get, set) => {
	const history = get(ActionHistoryAtom);
	if (history.length === 0) {
		return;
	}
	const entry = history[history.length - 1];
	set(ActionHistoryAtom, history.slice(0, -1));
	set(RedoStackAtom, (prev) => [...prev, entry.action]);

	const snap = entry.stateBefore;
	set(ObjectSelectionAtom, snap.selection);
	set(FocusedNodeAtom, snap.focusedNode);
	set(ActiveNodeAtom, snap.activeNode);
	set(NavigationPathAtom, snap.navigationPath);
	set(SelectedRelationshipsAtom, snap.selectedRelationships);

	// Restore query state for executeQuery actions
	if (entry.action.type === 'executeQuery') {
		// Abort any in-flight query request before restoring state
		// to avoid race render conditions
		const controller = get(QueryAbortControllerAtom);
		if (controller !== null && controller !== undefined) {
			controller.abort();
			set(QueryAbortControllerAtom, null);
		}
		set(ObjectsQueryRunningAtom, false);
		set(CypherQueryAtom, snap.cypherQuery ?? '');
		set(ObjectsQueryResultAtom, snap.queryResult ?? undefined);
	}

	if (snap.focusedNode === null) {
		set(FocusDirectionAtom, 'outgoing');
		set(DiscoveredOutgoingAtom, []);
		set(DiscoveredIncomingAtom, []);
		set(DiscoveryStatusAtom, 'idle');
		set(ExplorationErrorAtom, null);
		set(LoadingRelKeysAtom, new Set());
	} else {
		const effectiveNode = snap.activeNode ?? snap.focusedNode;
		await set(DiscoverForNodeAtom, effectiveNode);
	}
});

// ============================================================================
// Redo: pop action from redo stack, re-apply it, push new entry to history
// ============================================================================

export const RedoAtom: WritableAtom<null, [], Promise<void>> & {
	init: null;
} = atom(null, async (get, set) => {
	const redoStack = get(RedoStackAtom);
	if (redoStack.length === 0) {
		return;
	}
	const action = redoStack[redoStack.length - 1];
	set(RedoStackAtom, redoStack.slice(0, -1));

	const stateBefore: HistoryStateSnapshot = {
		selection: get(ObjectSelectionAtom),
		focusedNode: get(FocusedNodeAtom),
		selectedRelationships: get(SelectedRelationshipsAtom),
		navigationPath: get(NavigationPathAtom),
		activeNode: get(ActiveNodeAtom),
		...(action.type === 'executeQuery'
			? {
					cypherQuery: get(CypherQueryAtom),
					queryResult: get(ObjectsQueryResultAtom),
				}
			: {}),
	};

	switch (action.type) {
		case 'nodeDoubleClick': {
			const isFocusMode = get(FocusedNodeAtom) !== null;
			const baseNodeIds = get(BaseNodeIdsAtom);
			const isBaseNode = baseNodeIds.has(action.nodeData.id);
			if (isFocusMode) {
				await set(DrillIntoChildAtom, action.nodeData);
			} else if (isBaseNode) {
				await set(EnterFocusModeAtom, action.nodeData);
			}
			break;
		}
		case 'selectRelationship':
			await set(ToggleRelationshipAtom, {
				sourceNode: action.sourceNode,
				relType: action.relType,
				direction: action.direction,
			});
			set(ObjectSelectionAtom, {
				kind: 'edge',
				value: { relationshipType: action.relType },
				edgeId: `focus-${action.relType}`,
			});
			break;
		case 'removeRelationship':
			set(RemoveRelationshipAtom, action.key);
			break;
		case 'executeQuery':
			set(CypherQueryAtom, action.cypherQuery);
			set(ExecuteQueryAtom, 'none');
			break;
	}

	set(ActionHistoryAtom, (prev) => [...prev, { action, stateBefore }]);
});
